import { useContext } from 'react';
import { AxiosError } from 'axios';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { ApplyToJobPostWithResumeDataType } from '../../../models/dashboard/jobs/applyToJobPostWithResume.model';
import {
  ErrorHttpResponse,
  HttpConfig,
  SuccessHttpResponse,
} from '../../../models/https';
import { useAppSelector } from '../../../redux/store';
import useHttps from '../../useHttps';
import useMiniLoad from '../dashboardLoading/useMiniLoad';
import { ModalContext } from '../../../context/ModalContext';
import showInteractiveToast from '../../../components/Modals/ToastModals/InteractiveToast';
import { useNavigate } from 'react-router';
import { USERS_SETTINGS } from '../../../routes/routeConstants';
import { useSubscriptionAccess } from '../../../components/Subscription/SubscriptionAccessProvider';

const useApplyToJobPostWithResume = () => {
  const { requireFeature } = useSubscriptionAccess();
  const { authData } = useAppSelector((state) => state.auth);
  const request = useHttps();
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const { getMiniLoadData } = useMiniLoad();

  const modalContext = useContext(ModalContext);

  const onApplyToJobPostWithResumeError = (
    err: AxiosError<ErrorHttpResponse>
  ) => {
    let errorMessage =
      err?.response?.data?.message ?? 'Something went wrong while creating job';

    errorMessage = errorMessage.replace(/endpoint/gi, 'action'); // 🔁 Replace 'endpoint' with 'action'

    if (errorMessage.toLowerCase().includes('access level')) {
      showInteractiveToast({
        message: errorMessage,
        onConfirm: () => navigate(`/${USERS_SETTINGS}`),
      });
    }
    else if (errorMessage.toLowerCase().includes('Validation failed')) {
      toast.error("Please submit a pdf, doc or docx format or your CV/resume")
    }
    
    else {
      toast.error(errorMessage);
    }
  };

  const onApplyToJobPostWithResumeSuccess =
    (resetFormFn: (...args: any) => void) =>
    ({ data, message }: SuccessHttpResponse<any>) => {
      toast.success(message);
      resetFormFn();
      getMiniLoadData();
      modalContext?.displayModal(false);
    };

  const applyToJobPostWithResume = async (
    data: ApplyToJobPostWithResumeDataType,
    jobId: string,
    jobCreatorId: string | undefined,
    resetFormFn: (...args: any) => void
  ) => {
    if (authData?.id === jobCreatorId) {
      toast.error('You can not apply to a job you posted');
      return;
    }
    if (authData?.status !== 'ACTIVE') {
      toast.error(
        `You are ${authData?.status} and can not perform this action`
      );
      return;
    }

    const formData = new FormData();
    setLoading(true);
    if (!await requireFeature('canApplyToJobs')) { setLoading(false); return; }

    formData.append('firstName', authData?.firstName ?? '');
    formData.append('lastName', authData?.lastName ?? '');
    formData.append('email', authData?.email ?? '');
    formData.append('jobId', jobId);
    if (data.resumeFile instanceof File) {
      formData.append('resume', data.resumeFile);
    }
    // formData.append('resumeUrl', data.resumeUrl); //For Future enhancement

    const url: HttpConfig = {
      url: `jobs-service/applications/${jobId}/apply`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    request(
      url,
      onApplyToJobPostWithResumeSuccess(resetFormFn),
      setLoading,
      onApplyToJobPostWithResumeError
    );
  };

  return { applyToJobPostWithResume, loading };
};

export default useApplyToJobPostWithResume;
